/**
 * Give-get referral program: "Give 2 weeks, get 2 weeks".
 *
 * A paying subscriber shares story.maayot.com/r/CODE. The friend who signs up through it gets a
 * 14-day card trial instead of 5, and when the friend makes their first payment the referrer
 * gets half a month of their plan off their next bill (max 12 a year). The backend
 * (dev-backend/src/core/referral) owns every rule; this file only carries the code from the link
 * to the signup call. The API calls live in redux/actions/referralAction.ts.
 *
 * Not to be confused with siteReferral.ts, which records which www.maayot.com page a visitor
 * came from.
 */

import i18n from 'i18next'

export const REFERRAL_STORAGE_KEY = 'referral_code'
const REFERRAL_TIMESTAMP_KEY = 'referral_code_at'
/** A link clicked more than 30 days before signing up no longer counts. */
export const REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000
const CODE_PATTERN = /^[ABCDEFGHJKMNPQRSTUVWXYZ23456789]{8}$/

export const FRIEND_TRIAL_DAYS = 14
/** The affiliate application page, on the marketing site. */
export const AFFILIATE_PAGE_URL = 'https://www.maayot.com/affiliates'
export const DEFAULT_TRIAL_DAYS = 5

export function normalizeReferralCode(raw: string | null | undefined): string | null {
  const code = (raw || '').trim().toUpperCase()
  return CODE_PATTERN.test(code) ? code : null
}

export function storeReferralCode(code: string, now: number = Date.now()): void {
  try {
    localStorage.setItem(REFERRAL_STORAGE_KEY, code)
    localStorage.setItem(REFERRAL_TIMESTAMP_KEY, String(now))
  } catch {
    /* private mode: the friend simply signs up without the referral */
  }
}

/** The code from a referral link clicked in the last 30 days, or null. */
export function getStoredReferralCode(now: number = Date.now()): string | null {
  try {
    const code = normalizeReferralCode(localStorage.getItem(REFERRAL_STORAGE_KEY))
    const at = Number(localStorage.getItem(REFERRAL_TIMESTAMP_KEY))
    if (!code || !at || now - at > REFERRAL_TTL_MS) return null
    return code
  } catch {
    return null
  }
}

export function clearStoredReferralCode(): void {
  try {
    localStorage.removeItem(REFERRAL_STORAGE_KEY)
    localStorage.removeItem(REFERRAL_TIMESTAMP_KEY)
  } catch {
    /* nothing to clear */
  }
}

/** Extra signup fields: the referral code when there is a live one. */
export function referralSignupParams(): { referral_code?: string } {
  const code = getStoredReferralCode()
  return code ? { referral_code: code } : {}
}

export interface ReferralStatus {
  /** Paying, on the website or in the app: can share a link that gives friends 14 days free. */
  canRefer: boolean
  /** Paying on the website: also gets 2 weeks free per friend who subscribes. App payers gift only. */
  earnsReward?: boolean
  code: string | null
  link: string | null
  rewardsThisYear: number
  maxRewardsPerYear: number
  /** This learner's own trial length: 14 for an eligible referred friend, else 5. */
  trialDays: number
  friendTrialDays: number
}

/**
 * The message the share sheet and the copy button carry, in the sharer's interface language. No
 * "no card needed": the trial takes a card. In `common` because it is built outside React.
 */
export function referralShareText(link: string): string {
  return i18n.t('common:referralShareText', { days: FRIEND_TRIAL_DAYS, link })
}
